
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Coins, Settings, User, Plus, Minus, BarChart3 } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { PointsService, CountryPointsConfig, UserPoints, PointTransaction } from '@/services/points/PointsService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import UserPointsAnalytics from './UserPointsAnalytics';

interface CountryOption {
  code: string;
  name: string;
}

const AdminPointsControlPanel: React.FC = () => {
  const { user } = useAuth();
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [countryConfig, setCountryConfig] = useState<CountryPointsConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // User search and adjustment
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userPoints, setUserPoints] = useState<UserPoints | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadCountryConfig();
    }
  }, [selectedCountry]);

  const loadCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('code, name')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Error loading countries:', error);
      toast.error('Failed to load countries');
    }
  };

  const loadCountryConfig = async () => {
    if (!selectedCountry) return;
    
    setIsLoading(true);
    try {
      let config = await PointsService.getCountryConfig(selectedCountry);
      
      // If no config exists, create a default one
      if (!config) {
        config = {
          id: '',
          country_code: selectedCountry,
          points_enabled: false,
          poll_reward: 0.25,
          monthly_limit: 100.00,
          min_redemption_amount: 10.00,
          currency_symbol: '$',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      
      setCountryConfig(config);
    } catch (error) {
      console.error('Error loading country config:', error);
      toast.error('Failed to load country configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfigUpdate = async (updates: Partial<CountryPointsConfig>) => {
    if (!selectedCountry || !user?.id) return;

    setIsLoading(true);
    try {
      const result = await PointsService.updateCountryConfig(selectedCountry, updates);
      
      if (result.success) {
        toast.success('Configuration updated successfully');
        await loadCountryConfig();
      } else {
        toast.error(result.error || 'Failed to update configuration');
      }
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Failed to update configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const searchUser = async () => {
    if (!searchEmail.trim()) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, name')
        .eq('email', searchEmail.trim())
        .single();

      if (error || !data) {
        toast.error('User not found');
        return;
      }

      setSelectedUser(data);
      
      // Load user points for selected country
      if (selectedCountry) {
        const points = await PointsService.getUserPoints(data.id, selectedCountry);
        setUserPoints(points);
      }
    } catch (error) {
      console.error('Error searching user:', error);
      toast.error('Error searching for user');
    }
  };

  const handlePointsAdjustment = async () => {
    if (!selectedUser || !selectedCountry || !user?.id || !adjustmentAmount.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount) || amount === 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsLoading(true);
    try {
      const result = await PointsService.adminAdjustPoints(
        selectedUser.id,
        amount,
        adjustmentDescription || `Admin adjustment: ${amount > 0 ? 'added' : 'removed'} ${Math.abs(amount)} points`,
        selectedCountry,
        user.id
      );

      if (result.success) {
        toast.success('Points adjusted successfully');
        setAdjustmentAmount('');
        setAdjustmentDescription('');
        
        // Reload user points
        const updatedPoints = await PointsService.getUserPoints(selectedUser.id, selectedCountry);
        setUserPoints(updatedPoints);
      } else {
        toast.error(result.error || 'Failed to adjust points');
      }
    } catch (error) {
      console.error('Error adjusting points:', error);
      toast.error('Failed to adjust points');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="configuration" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="management">User Management</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="configuration" className="space-y-6">
          {/* Country Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Country Points Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="country">Select Country</Label>
                  <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>Choose a country</SelectItem>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Country Configuration Form */}
                {selectedCountry && countryConfig && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="points-enabled">Points System Enabled</Label>
                        <Switch
                          id="points-enabled"
                          checked={countryConfig.points_enabled}
                          onCheckedChange={(enabled) => handleConfigUpdate({ points_enabled: enabled })}
                          disabled={isLoading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="poll-reward">Poll Vote Reward</Label>
                        <Input
                          id="poll-reward"
                          type="number"
                          step="0.01"
                          value={countryConfig.poll_reward}
                          onChange={(e) => setCountryConfig(prev => prev ? { ...prev, poll_reward: parseFloat(e.target.value) || 0 } : null)}
                          onBlur={() => countryConfig && handleConfigUpdate({ poll_reward: countryConfig.poll_reward })}
                          disabled={isLoading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="monthly-limit">Monthly Limit</Label>
                        <Input
                          id="monthly-limit"
                          type="number"
                          step="0.01"
                          value={countryConfig.monthly_limit}
                          onChange={(e) => setCountryConfig(prev => prev ? { ...prev, monthly_limit: parseFloat(e.target.value) || 0 } : null)}
                          onBlur={() => countryConfig && handleConfigUpdate({ monthly_limit: countryConfig.monthly_limit })}
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="min-redemption">Minimum Redemption</Label>
                        <Input
                          id="min-redemption"
                          type="number"
                          step="0.01"
                          value={countryConfig.min_redemption_amount}
                          onChange={(e) => setCountryConfig(prev => prev ? { ...prev, min_redemption_amount: parseFloat(e.target.value) || 0 } : null)}
                          onBlur={() => countryConfig && handleConfigUpdate({ min_redemption_amount: countryConfig.min_redemption_amount })}
                          disabled={isLoading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="currency-symbol">Currency Symbol</Label>
                        <Input
                          id="currency-symbol"
                          value={countryConfig.currency_symbol}
                          onChange={(e) => setCountryConfig(prev => prev ? { ...prev, currency_symbol: e.target.value } : null)}
                          onBlur={() => countryConfig && handleConfigUpdate({ currency_symbol: countryConfig.currency_symbol })}
                          disabled={isLoading}
                        />
                      </div>

                      <div className="pt-4">
                        <Badge variant={countryConfig.points_enabled ? "default" : "secondary"}>
                          {countryConfig.points_enabled ? "Active" : "Disabled"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}

                {/* Loading state for country config */}
                {selectedCountry && !countryConfig && isLoading && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="animate-pulse space-y-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="management" className="space-y-6">
          {/* User Points Management */}
          {selectedCountry && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  User Points Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* User Search */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search by email address"
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchUser()}
                    />
                    <Button onClick={searchUser} disabled={!searchEmail.trim()}>
                      Search
                    </Button>
                  </div>

                  {/* Selected User Info */}
                  {selectedUser && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-medium">{selectedUser.name || 'No name'}</h4>
                          <p className="text-sm text-gray-600">{selectedUser.email}</p>
                        </div>
                        {userPoints && selectedCountry && countryConfig && (
                          <div className="text-right">
                            <div className="text-lg font-bold text-green-600">
                              {countryConfig.currency_symbol}{userPoints.total_points.toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-600">Current Balance</div>
                          </div>
                        )}
                      </div>

                      {/* Points Adjustment */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                          <Label htmlFor="adjustment-amount">Adjustment Amount</Label>
                          <Input
                            id="adjustment-amount"
                            type="number"
                            step="0.01"
                            placeholder="Use + for add, - for subtract"
                            value={adjustmentAmount}
                            onChange={(e) => setAdjustmentAmount(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="adjustment-description">Description</Label>
                          <Input
                            id="adjustment-description"
                            placeholder="Reason for adjustment"
                            value={adjustmentDescription}
                            onChange={(e) => setAdjustmentDescription(e.target.value)}
                          />
                        </div>
                        <div className="flex items-end">
                          <Button 
                            onClick={handlePointsAdjustment}
                            disabled={isLoading || !adjustmentAmount.trim()}
                            className="w-full"
                          >
                            {isLoading ? 'Processing...' : 'Adjust Points'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <UserPointsAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPointsControlPanel;
